import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
  useEquipCosmetic as useEquipCosmeticMutation,
  useUnequipCosmetic as useUnequipCosmeticMutation,
  familyKeys,
  resolveErrorMessage,
  type EquipCosmeticVars,
  type CosmeticCatalogResponse,
  type EquippedCosmeticsResponse,
} from '@kidshome/api-contract';
import { useToast } from '@/shared/ui';
import { tDynamic } from '@/shared/lib/i18n';

type EquipContext = { previous: EquippedCosmeticsResponse | undefined };

/**
 * `equip-cosmetic` — the owned-item Equip / Equipped toggle. Equipping a NEW item swaps out whatever
 * was active in its slot (the server handles the swap); tapping the already-equipped item unequips it
 * (back to the default look). Neither is a money move, so no idempotency key.
 *
 * OPTIMISTIC UPDATE: the `cosmeticsEquipped` cache is updated IMMEDIATELY on tap so the hero figure
 * on the wardrobe page reacts in the same frame (matching the platform preview behavior). The server
 * response confirms + `onSettled` revalidates to server truth; errors roll back the cache.
 *
 * Concurrency: in-flight items are tracked as a SET (each card's busy state is independent), and the
 * settle/error callbacks live on the MUTATIONS (not per `mutate` call) — TanStack Query drops a call's
 * own callbacks when `mutate` fires again before the first settles, which would strand the first card
 * busy forever and skip its invalidation.
 */
export function useEquipCosmetic(): {
  /** Toggle equip for `itemId`: equip it, or unequip it when `currentlyEquipped`. */
  toggle: (itemId: string, currentlyEquipped: boolean) => void;
  /** The item ids currently in flight (so each card can show its own pending state). */
  pendingItemIds: ReadonlySet<string>;
} {
  const { t } = useTranslation(['family', 'errors']);
  const toast = useToast();
  const qc = useQueryClient();
  const [pendingItemIds, setPendingItemIds] = useState<ReadonlySet<string>>(new Set());

  // Optimistically add the item to the equipped set, pushing out any previous item in the same place.
  // We look up the item's place from the catalog cache (already in-memory when the kid is in the shop).
  const onMutateEquip = useCallback(
    async (vars: EquipCosmeticVars): Promise<EquipContext> => {
      await qc.cancelQueries({ queryKey: familyKeys.cosmeticsEquipped() });
      const previous = qc.getQueryData<EquippedCosmeticsResponse>(familyKeys.cosmeticsEquipped());
      if (previous) {
        const catalog = qc.getQueryData<CosmeticCatalogResponse>(familyKeys.cosmeticsCatalog());
        const item = catalog?.items.find((i) => i.itemId === vars.itemId);
        if (item) {
          qc.setQueryData<EquippedCosmeticsResponse>(familyKeys.cosmeticsEquipped(), {
            ...previous,
            equipped: [
              ...previous.equipped.filter((e) => e.place !== item.place),
              { place: item.place, itemId: item.itemId, nameKey: item.nameKey },
            ],
          });
        }
      }
      return { previous };
    },
    [qc],
  );

  // Optimistically remove the item from the equipped set.
  const onMutateUnequip = useCallback(
    async (vars: EquipCosmeticVars): Promise<EquipContext> => {
      await qc.cancelQueries({ queryKey: familyKeys.cosmeticsEquipped() });
      const previous = qc.getQueryData<EquippedCosmeticsResponse>(familyKeys.cosmeticsEquipped());
      if (previous) {
        qc.setQueryData<EquippedCosmeticsResponse>(familyKeys.cosmeticsEquipped(), {
          ...previous,
          equipped: previous.equipped.filter((e) => e.itemId !== vars.itemId),
        });
      }
      return { previous };
    },
    [qc],
  );

  const onError = useCallback(
    (error: unknown, vars: EquipCosmeticVars, context: EquipContext | undefined): void => {
      // Roll back ONLY the affected item's place from the snapshot — a wholesale restore would
      // clobber a concurrent optimistic equip on a different place still in flight. We look up the
      // place the same way `onMutateEquip` does (catalog cache), then merge just that place's prior
      // entry back into the CURRENT cache (mirrors the per-row rollback in use-check-habit.ts).
      //
      // Unlike habits (isolated by their own stable id), TWO DIFFERENT items can share a place, so a
      // second toggle on the same place can resolve before this one. If it already did, the place's
      // CURRENT entry no longer matches what THIS mutation's own optimistic write left behind (`vars`'s
      // itemId, present for an equip or absent for an unequip) — that means a later, successful mutation
      // already owns the place, so we must skip the restore rather than clobber it with stale data.
      if (context?.previous !== undefined) {
        const catalog = qc.getQueryData<CosmeticCatalogResponse>(familyKeys.cosmeticsCatalog());
        const place = catalog?.items.find((i) => i.itemId === vars.itemId)?.place;
        if (place !== undefined) {
          const prevEntryForPlace = context.previous.equipped.find((e) => e.place === place);
          qc.setQueryData<EquippedCosmeticsResponse>(familyKeys.cosmeticsEquipped(), (cur) => {
            if (!cur) return cur;
            const curEntryForPlace = cur.equipped.find((e) => e.place === place);
            const ownedByLaterMutation =
              curEntryForPlace !== undefined && curEntryForPlace.itemId !== vars.itemId;
            if (ownedByLaterMutation) return cur;
            return {
              ...cur,
              equipped: [
                ...cur.equipped.filter((e) => e.place !== place),
                ...(prevEntryForPlace ? [prevEntryForPlace] : []),
              ],
            };
          });
        }
      }
      const { key, params } = resolveErrorMessage(error);
      toast.error(tDynamic(t, key, params));
    },
    [toast, t, qc],
  );

  const onSettled = useCallback(
    (_data: unknown, _error: unknown, vars: EquipCosmeticVars): void => {
      // Clear only THIS item's busy flag — other toggles may still be in flight.
      setPendingItemIds((prev) => {
        const next = new Set(prev);
        next.delete(vars.itemId);
        return next;
      });
      void qc.invalidateQueries({ queryKey: familyKeys.cosmeticsCatalog() });
      void qc.invalidateQueries({ queryKey: familyKeys.cosmeticsEquipped() });
    },
    [qc],
  );

  const equip = useEquipCosmeticMutation<EquipContext>({
    onMutate: onMutateEquip,
    onError,
    onSettled,
  });
  const unequip = useUnequipCosmeticMutation<EquipContext>({
    onMutate: onMutateUnequip,
    onError,
    onSettled,
  });

  const toggle = useCallback(
    (itemId: string, currentlyEquipped: boolean): void => {
      setPendingItemIds((prev) => new Set(prev).add(itemId));
      const mutation = currentlyEquipped ? unequip : equip;
      mutation.mutate({ itemId });
    },
    [equip, unequip],
  );

  return { toggle, pendingItemIds };
}
